import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import type { Pageable, WrapperPageableResponse, WrapperResponse } from './service-common';
import type { CurrencyDTO, EntityStateValue } from '../core/models';

export interface BankStatementLayout {
  headerRow?: number;
  delimiter?: string;
  dateFormat?: string;
  dateColumn?: string;
  descriptionColumn?: string;
  referenceColumn?: string;
  debitColumn?: string;
  creditColumn?: string;
  amountColumn?: string;
  signColumn?: string;
  balanceColumn?: string;
  counterpartNameColumn?: string;
  counterpartClabeColumn?: string;
  trackCodeColumn?: string;
  orderingBankColumn?: string;
  paymentFormColumn?: string;
}

export interface PaymentFormAliasDTO {
  id?: number;
  alias?: string;
  paymentFormId?: number;
  paymentFormLabel?: string;
}

export interface BankAccountDTO {
  id?: number;
  alias?: string;
  bankName?: string;
  clabe?: string;
  currency?: CurrencyDTO;
  settlementAccount?: boolean;
  statementLayout?: BankStatementLayout;
  paymentFormAliases?: PaymentFormAliasDTO[];
  reconciledUntil?: string | null;
  state?: EntityStateValue;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateBankAccountRequest {
  alias: string;
  bankName: string;
  clabe: string;
  currencyId: number;
  settlementAccount?: boolean;
  statementLayout?: BankStatementLayout;
  paymentFormAliases?: { id?: number; alias: string; paymentFormId: number }[];
}

@Injectable({ providedIn: 'root' })
export class BankAccountService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/bank-accounts`;

  search(
    spec: Record<string, unknown> = {},
    pageable: Pageable = { page: 0, size: 20 }
  ): Observable<WrapperPageableResponse<BankAccountDTO>> {
    return this.http.get<WrapperPageableResponse<BankAccountDTO>>(this.apiUrl, {
      params: this.buildParams({ ...spec, ...pageable })
    });
  }

  list(
    spec: Record<string, unknown> = {},
    pageable: Pageable = { page: 0, size: 50 }
  ): Observable<WrapperPageableResponse<BankAccountDTO>> {
    return this.http.get<WrapperPageableResponse<BankAccountDTO>>(`${this.apiUrl}/list`, {
      params: this.buildParams({ ...spec, ...pageable })
    });
  }

  getById(id: number): Observable<WrapperResponse<BankAccountDTO>> {
    return this.http.get<WrapperResponse<BankAccountDTO>>(`${this.apiUrl}/${id}`);
  }

  create(request: CreateBankAccountRequest): Observable<WrapperResponse<BankAccountDTO>> {
    return this.http.post<WrapperResponse<BankAccountDTO>>(this.apiUrl, request);
  }

  update(id: number, request: CreateBankAccountRequest): Observable<WrapperResponse<BankAccountDTO>> {
    return this.http.put<WrapperResponse<BankAccountDTO>>(`${this.apiUrl}/${id}`, request);
  }

  delete(id: number): Observable<WrapperResponse<void>> {
    return this.http.delete<WrapperResponse<void>>(`${this.apiUrl}/${id}`);
  }

  private buildParams(raw: Record<string, unknown>): HttpParams {
    let params = new HttpParams();
    Object.entries(raw).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (Array.isArray(value)) {
        value.forEach(item => {
          params = params.append(key, String(item));
        });
        return;
      }
      params = params.append(key, String(value));
    });
    return params;
  }
}
